/**
 * MCP bridge (browser side) — connects this tab to the local `mcp/server.mjs`
 * hub so external MCP clients (Claude Code, Claude Desktop) can drive the
 * document. Protocol: EventSource for inbound tool calls, POST for the tool
 * registration and call results.
 *
 * Every call executes through AssistantToolbox.allTools() — the identical
 * surface the built-in copilot uses — so external edits land in undo
 * history, autosave, and the timeline exactly like GUI actions.
 *
 * The bridge is opportunistic: it retries quietly with backoff, so it works
 * whether the hub was started before or after the tab (and costs one failed
 * request per few minutes when no hub is running).
 */

import { classifyToolError, type AssistantToolbox } from './tools';

const BASE = 'http://127.0.0.1:8765';
const RETRY_MIN_MS = 5_000;
const RETRY_MAX_MS = 120_000;

export interface McpBridgeStatus {
  connected: boolean;
}

export function startMcpBridge(
  toolbox: AssistantToolbox,
  onStatus?: (s: McpBridgeStatus) => void,
): void {
  let retryMs = RETRY_MIN_MS;
  let source: EventSource | null = null;
  /** Serializes inbound tool calls so CadApi ops never interleave. */
  let callQueue: Promise<void> = Promise.resolve();

  const post = (path: string, body: unknown): Promise<Response> =>
    fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });

  const register = async (): Promise<void> => {
    const tools = toolbox.allTools().map((t) => ({
      name: t.name,
      description: t.description,
      inputSchema: t.parameters,
    }));
    await post('/register', { tools });
  };

  const connect = (): void => {
    source = new EventSource(`${BASE}/events`);

    source.onopen = () => {
      retryMs = RETRY_MIN_MS;
      void register();
      onStatus?.({ connected: true });
    };

    source.onmessage = (ev) => {
      // Serialize tool calls: CadApi mutates one shared document and assumes a
      // stable doc across each op, so concurrent calls (or a call racing a
      // project switch) must not interleave. Chain them one at a time.
      const run = async (): Promise<void> => {
        let id: unknown;
        try {
          const call = JSON.parse(ev.data) as { id: unknown; name: string; args: unknown };
          id = call.id;
          const tool = toolbox.allTools().find((t) => t.name === call.name);
          if (!tool) throw new Error(`unknown tool "${call.name}"`);
          const result = await tool.execute(
            (call.args ?? {}) as Record<string, unknown>,
            toolbox.api,
          );
          await post('/result', { id, result: result ?? 'done' });
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          const hint = classifyToolError(message);
          await post('/result', {
            id,
            error: hint ? `${message} [${hint.code}] Next step: ${hint.suggestion}` : message,
          }).catch(() => undefined);
        }
      };
      callQueue = callQueue.then(run, run);
    };

    source.onerror = () => {
      source?.close();
      source = null;
      onStatus?.({ connected: false });
      setTimeout(connect, retryMs);
      retryMs = Math.min(retryMs * 2, RETRY_MAX_MS);
    };
  };

  // Custom tools defined later must reach already-connected clients too.
  const prevOnChange = toolbox.onChange;
  toolbox.onChange = () => {
    prevOnChange?.();
    if (source && source.readyState === EventSource.OPEN) void register();
  };

  connect();
}
